import React, { useEffect, useState } from 'react';
import { hyphenateFinnish } from '../../hyphenationService';
import { forNotoEmoji, parseClue } from './clueUtils';
import ClueIcons from './ClueIcons';

const TEXT_FONT = "'Geologica', 'Noto Emoji', sans-serif";
const BORDER = 1;
const DIVIDER = 1;

// Mirrors clueCellSplitsVertically in the app's cw_clue_cell.dart
const splitsVertically = (acrossClue, downClue, expansion) =>
  expansion > 0 && !(parseClue(acrossClue).type !== 'icons' && parseClue(downClue).type !== 'icons');

// Mirrors clueCellSplitFlex: an icon gets more room than the one-line text beside it
const splitFlex = (acrossClue, downClue) => {
  const acrossIsIcon = parseClue(acrossClue).type === 'icons';
  const downIsIcon = parseClue(downClue).type === 'icons';
  if (acrossIsIcon === downIsIcon) return { across: 1, down: 1 };
  return acrossIsIcon ? { across: 3, down: 2 } : { across: 2, down: 3 };
};

// Mirrors _calculateTextSize in cw_clue_cell.dart
const calculateTextSize = (charCount, baseFontSize) => {
  const multiplier =
    charCount === 1 ? 2.5 :
    charCount === 2 ? 2.0 :
    charCount === 3 ? 1.5 :
    charCount <= 12 ? 1.0 + (charCount - 4) / 8 * -0.3 :
    0.7;
  return Math.min(Math.max(baseFontSize * multiplier, 8), 50);
};

let measureContext = null;
const measureTextWidth = (text, fontSize) => {
  measureContext = measureContext || document.createElement('canvas').getContext('2d');
  measureContext.font = `400 ${fontSize}px ${TEXT_FONT}`;
  return measureContext.measureText(text).width;
};

// Mirrors _fitTextSize: a single line has to fit the height, and a short word the width too
const fitTextSize = (text, baseFontSize, maxLines, maxWidth, maxHeight) => {
  let fontSize = calculateTextSize(text.length, baseFontSize);
  const isShort = text.length <= 4 && !text.includes(' ');
  if (maxLines === 1 || isShort) fontSize = Math.min(fontSize, maxHeight * 0.9);
  if (isShort) {
    const width = measureTextWidth(text, 100);
    if (width > 0) fontSize = Math.min(fontSize, maxWidth * 0.95 / width * 100);
  }
  return fontSize;
};

// Text widths are measured on a canvas, so re-measure once Geologica has loaded
const useFontsLoaded = () => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    if (!document.fonts) return undefined;
    const bump = () => setVersion(v => v + 1);
    document.fonts.ready.then(bump);
    document.fonts.addEventListener('loadingdone', bump);
    return () => document.fonts.removeEventListener('loadingdone', bump);
  }, []);
};

// Convert editor placeholder ︲ back to real soft hyphen for rendering.
// If ︲ immediately follows a visible hyphen, drop it — the hyphen already
// acts as a break point and adding ­ would render a double dash.
const normalizeClue = (clue) => (clue || '').replace(/-\u{FE32}/gu, '-').replace(/\u{FE32}/gu, '­');

// Mirrors _ClueContent in cw_clue_cell.dart for a box of boxWidth × boxHeight px.
const ClueContent = ({ clue, cellSize, boxWidth, boxHeight, sizeMultiplier = 1, maxLines = 3, expandMultiplier = 1 }) => {
  useFontsLoaded();
  const normalized = normalizeClue(clue);
  const parsed = parseClue(normalized);
  const padding = maxLines === 3 ? 2 : 1;
  const maxWidth = boxWidth - padding * 2;
  const maxHeight = boxHeight - padding * 2;
  const size = Math.min(Math.max(cellSize * 0.7, 16), 60);

  const wrapper = {
    width: `${boxWidth}px`,
    height: `${boxHeight}px`,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: `${padding}px`,
    boxSizing: 'border-box',
    color: '#333',
    overflow: parsed.type === 'icons' ? 'visible' : 'hidden',
  };

  if (parsed.type === 'icons') {
    return (
      <div style={wrapper}>
        <ClueIcons parsed={parsed} size={size} sizeMultiplier={sizeMultiplier} maxWidth={maxWidth} maxHeight={maxHeight} />
      </div>
    );
  }

  const fontSizeBoost = expandMultiplier > 1 && sizeMultiplier === 1 ? 1.2 : 1;
  const text = forNotoEmoji(parsed.content);
  const fontSize = fitTextSize(text, cellSize * 0.33 * fontSizeBoost, maxLines, maxWidth, maxHeight);
  const displayContent = text.includes('­') ? text : hyphenateFinnish(text);

  return (
    <div style={wrapper}>
      <span
        lang="fi"
        className="in-grid-clue-text"
        style={{
          fontFamily: TEXT_FONT,
          fontSize: `${fontSize}px`,
          lineHeight: 1,
          textAlign: 'center',
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          // 'normal' (not 'break-word'): don't force-split a word with no
          // break point. Flutter only breaks at soft hyphens, so neither
          // should the preview — a word that won't hyphenate should overflow
          // here too, making the missing break visible.
          wordBreak: 'normal',
          overflowWrap: 'normal',
          // 'manual' (not 'auto'): the browser breaks ONLY at the soft hyphens
          // we inject via Hypher / manual ﹣ markers — never at its own
          // dictionary points. This matches Flutter, which has no CSS fallback,
          // so the preview truthfully shows whether a word actually hyphenates.
          hyphens: 'manual',
          WebkitHyphens: 'manual',
          msHyphens: 'manual',
        }}
      >
        {displayContent}
      </span>
    </div>
  );
};

// Renders clue content for a single black/border cell, mirroring Flutter's ClueCell widget.
// cellSize is the rendered pixel size of one grid cell (default 40 to match .grid-cell CSS).
// emptyRight / emptyLeft control horizontal cell expansion (steal adjacent empty border cells).
const InGridClue = ({ acrossClue, downClue, cellSize = 40, emptyRight = 0, emptyLeft = 0 }) => {
  const hasBoth = acrossClue != null && downClue != null;
  const horizontalExpansion = emptyRight + emptyLeft;
  const expandHorizontal = horizontalExpansion > 0;
  const widthMultiplier = 1 + horizontalExpansion;
  const totalWidth = cellSize * widthMultiplier;
  const innerWidth = totalWidth - BORDER * 2;
  const innerHeight = cellSize - BORDER * 2;
  const content = (props) => <ClueContent cellSize={cellSize} {...props} />;

  let inner;
  if (!hasBoth) {
    inner = content({ clue: acrossClue ?? downClue ?? '', boxWidth: innerWidth, boxHeight: innerHeight, expandMultiplier: widthMultiplier });
  } else if (splitsVertically(acrossClue, downClue, horizontalExpansion)) {
    const halfWidth = (innerWidth - DIVIDER) / 2;
    inner = (
      <div style={{ display: 'flex', width: '100%', height: '100%' }}>
        {content({ clue: downClue, boxWidth: halfWidth, boxHeight: innerHeight, expandMultiplier: widthMultiplier / 2 })}
        <div style={{ width: `${DIVIDER}px`, background: '#aaa', flexShrink: 0 }} />
        {content({ clue: acrossClue, boxWidth: halfWidth, boxHeight: innerHeight, expandMultiplier: widthMultiplier / 2 })}
      </div>
    );
  } else {
    const flex = splitFlex(acrossClue, downClue);
    const unit = (innerHeight - DIVIDER) / (flex.across + flex.down);
    inner = (
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
        {content({ clue: acrossClue, boxWidth: innerWidth, boxHeight: unit * flex.across, maxLines: 1, expandMultiplier: widthMultiplier, sizeMultiplier: flex.across > flex.down ? 0.6 : 0.5 })}
        <div style={{ height: `${DIVIDER}px`, background: '#aaa', flexShrink: 0 }} />
        {content({ clue: downClue, boxWidth: innerWidth, boxHeight: unit * flex.down, maxLines: 1, expandMultiplier: widthMultiplier, sizeMultiplier: flex.down > flex.across ? 0.6 : 0.5 })}
      </div>
    );
  }

  if (!expandHorizontal) {
    return <div style={{ width: '100%', height: '100%' }}>{inner}</div>;
  }

  // Expanded: absolutely positioned div overflows into claimed neighbour cells,
  // stepping out 1px on each side to sit flush with the outer grid border lines.
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div style={{
        position: 'absolute',
        left: `${-(emptyLeft * cellSize) - BORDER}px`,
        top: `-${BORDER}px`,
        width: `${totalWidth}px`,
        height: `${cellSize}px`,
        background: '#e0e0e0',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        border: `${BORDER}px solid #333`,
        boxSizing: 'border-box',
      }}>
        {inner}
      </div>
    </div>
  );
};

export default InGridClue;
